import { Box, CircularProgress, Typography } from '@material-ui/core';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import HeadsetMicIcon from '@mui/icons-material/HeadsetMic';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import SnackbarOmni from 'allGlobalComponents/SnackbarOmni';
import PageHeading from 'allGlobalComponents/PageHeading';
import TableOmniNewAnimated from 'allGlobalComponents/TableOmniNewAnimated';
import DateFilterWithExport from 'allGlobalComponents/DateFilterWithExport';
import ButtonOmni from 'allGlobalComponents/ButtonOmni';
import ModalOmni from 'allGlobalComponents/ModalOmni';
import FormFieldOmni from 'allGlobalComponents/FormFieldOmni';
import DeleteConfirmationDialog from 'allGlobalComponents/DeleteConfirmationDoalog';
import React, { useEffect, useState, useMemo } from 'react';
import { useSnackbar, useApi, useExport } from 'app/hooks';

const useStyles = makeStyles(theme => ({
  content: {
    width: '100%',
    padding: '0 16px'
  },
  headingWrapper: {
    flex: 1,
    display: 'flex',
    alignItems: 'center'
  },
  heading: {
    fontSize: '18px !important',
    fontWeight: '600 !important',
    color: theme.palette.text.primary
  },
  formContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2.5),
    padding: theme.spacing(2)
  },
  formRow: {
    display: 'flex',
    gap: theme.spacing(2),
    flexWrap: 'wrap',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column'
    }
  },
  formActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: theme.spacing(2),
    width: '100%'
  }
}));

const ACTIVE_STATUS_OPTIONS = [
  { value: 'true', label: 'Active' },
  { value: 'false', label: 'Inactive' }
];

const initialCreateForm = {
  userId: '',
  extension: '',
  sipUsername: '',
  sipPassword: '',
  sipRealm: '',
  wsServer: '',
  displayName: '',
  isActive: true
};

const initialEditForm = {
  id: null,
  userId: '',
  extension: '',
  sipUsername: '',
  sipPassword: '',
  sipRealm: '',
  wsServer: '',
  displayName: '',
  isActive: true
};

function CallCenterUsers({ data }) {
  const classes = useStyles();
  const theme = useTheme();

  const clientId = data?.id;

  // Custom Hooks
  const { snackbar, showError, showSuccess, showWarning } = useSnackbar();
  const { isLoading, request } = useApi();
  const { isExporting, handleExport } = useExport();

  // Local State
  const [accounts, setAccounts] = useState([]);
  const [agents, setAgents] = useState([]);
  const [refreshKey, setRefreshKey] = useState(0);

  // Delete Dialog State
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Create Dialog State
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [createFormData, setCreateFormData] = useState(initialCreateForm);

  // Edit Dialog State
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editFormData, setEditFormData] = useState(initialEditForm);

  // Fetch agents list for Agent dropdown + user_id → name map
  useEffect(() => {
    if (!clientId) return;
    request({
      module: 'CompanyAgent',
      method: 'get_paginated',
      payload: {
        client_id: clientId,
        limit: 50,
        page: 0
      },
      onSuccess: (response) => {
        const list = response.data.data.agentList || [];
        setAgents(list);
      },
      onError: () => { }
    });
  }, [clientId]);

  // Fetch PBX accounts
  useEffect(() => {
    if (!clientId) return;
    request({
      module: 'pbx',
      method: 'pbxAccountList',
      payload: { clientId },
      onSuccess: (response) => {
        const res = response.data.data;
        setAccounts(res.accounts || []);
      },
      onError: (message) => showError(message || 'Error fetching PBX accounts')
    });
  }, [refreshKey, clientId]);

  // Map agent id → display name (firstname + lastname, fallback to username)
  const agentsById = useMemo(() => {
    const map = {};
    agents.forEach(a => {
      map[String(a.id)] = a.username;
    });
    return map;
  }, [agents]);

  // ---- Delete handlers ----
  const handleDeleteClick = (row) => {
    setSelectedAccount(row);
    setDeleteDialogOpen(true);
  };

  const handleDeleteClose = () => {
    if (!isDeleting) {
      setDeleteDialogOpen(false);
      setSelectedAccount(null);
    }
  };

  const handleDeleteConfirm = () => {
    if (!selectedAccount) return;

    setIsDeleting(true);
    request({
      module: 'pbx',
      method: 'pbxAccountDelete',
      payload: {
        clientId,
        accountId: Number.parseInt(selectedAccount.id)
      },
      onSuccess: () => {
        setIsDeleting(false);
        setDeleteDialogOpen(false);
        setSelectedAccount(null);
        showSuccess('PBX account deleted successfully');
        setRefreshKey(prev => prev + 1);
      },
      onError: (message) => {
        setIsDeleting(false);
        showError(message || 'Error deleting PBX account');
      }
    });
  };

  // ---- Create handlers ----
  const handleCreateClick = () => {
    setCreateFormData(initialCreateForm);
    setCreateDialogOpen(true);
  };

  const handleCreateClose = () => {
    if (!isCreating) {
      setCreateDialogOpen(false);
      setCreateFormData(initialCreateForm);
    }
  };

  const handleCreateFormChange = (field, value) => {
    setCreateFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleCreateConfirm = () => {
    if (!createFormData.userId) {
      showError('Agent is required');
      return;
    }
    if (!createFormData.extension.trim()) {
      showError('Extension is required');
      return;
    }
    if (!createFormData.sipUsername.trim()) {
      showError('SIP Username is required');
      return;
    }
    if (!createFormData.sipPassword.trim()) {
      showError('SIP Password is required');
      return;
    }
    if (!createFormData.sipRealm.trim()) {
      showError('SIP Realm is required');
      return;
    }
    if (!createFormData.wsServer.trim()) {
      showError('WS Server is required');
      return;
    }
    if (!createFormData.displayName.trim()) {
      showError('Display Name is required');
      return;
    }

    setIsCreating(true);

    const payload = {
      clientId,
      userId: Number.parseInt(createFormData.userId),
      extension: createFormData.extension,
      sipUsername: createFormData.sipUsername,
      sipPassword: createFormData.sipPassword,
      sipRealm: createFormData.sipRealm,
      wsServer: createFormData.wsServer,
      displayName: createFormData.displayName,
      isActive: !!createFormData.isActive
    };

    request({
      module: 'pbx',
      method: 'pbxAccountCreate',
      payload,
      onSuccess: () => {
        setIsCreating(false);
        setCreateDialogOpen(false);
        setCreateFormData(initialCreateForm);
        showSuccess('PBX account created successfully');
        setRefreshKey(prev => prev + 1);
      },
      onError: (message) => {
        setIsCreating(false);
        showError(message || 'Error creating PBX account');
      }
    });
  };

  // ---- Edit handlers ----
  const handleEditClick = (row) => {
    setEditFormData({
      id: row.id,
      userId: row.user_id ? String(row.user_id) : '',
      extension: row.extension || '',
      sipUsername: row.sip_username || '',
      sipPassword: '',
      sipRealm: row.sip_realm || '',
      wsServer: row.ws_server || '',
      displayName: row.display_name || '',
      isActive: !!row.is_active
    });
    setEditDialogOpen(true);
  };

  const handleEditClose = () => {
    if (!isEditing) {
      setEditDialogOpen(false);
      setEditFormData(initialEditForm);
    }
  };

  const handleEditFormChange = (field, value) => {
    setEditFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleEditConfirm = () => {
    if (!editFormData.userId) {
      showError('Agent is required');
      return;
    }
    if (!editFormData.extension.trim()) {
      showError('Extension is required');
      return;
    }
    if (!editFormData.sipUsername.trim()) {
      showError('SIP Username is required');
      return;
    }
    if (!editFormData.sipRealm.trim()) {
      showError('SIP Realm is required');
      return;
    }
    if (!editFormData.wsServer.trim()) {
      showError('WS Server is required');
      return;
    }
    if (!editFormData.displayName.trim()) {
      showError('Display Name is required');
      return;
    }

    setIsEditing(true);

    const payload = {
      clientId,
      accountId: Number.parseInt(editFormData.id),
      userId: Number.parseInt(editFormData.userId),
      extension: editFormData.extension,
      sipUsername: editFormData.sipUsername,
      sipRealm: editFormData.sipRealm,
      wsServer: editFormData.wsServer,
      displayName: editFormData.displayName,
      isActive: !!editFormData.isActive
    };

    // Only send password if user entered a new one
    if (editFormData.sipPassword.trim()) {
      payload.sipPassword = editFormData.sipPassword;
    }

    request({
      module: 'pbx',
      method: 'pbxAccountUpdate',
      payload,
      onSuccess: () => {
        setIsEditing(false);
        setEditDialogOpen(false);
        setEditFormData(initialEditForm);
        showSuccess('PBX account updated successfully');
        setRefreshKey(prev => prev + 1);
      },
      onError: (message) => {
        setIsEditing(false);
        showError(message || 'Error updating PBX account');
      }
    });
  };

  // ---- Export ----
  const exportColumns = [
    { header: 'S.No', key: '_index' },
    { header: 'ID', key: 'id' },
    { header: 'Agent', key: 'user_id', transform: (val) => agentsById[String(val)] || val || '-' },
    { header: 'Extension', key: 'extension' },
    { header: 'SIP Username', key: 'sip_username' },
    { header: 'SIP Realm', key: 'sip_realm' },
    { header: 'WS Server', key: 'ws_server' },
    { header: 'Display Name', key: 'display_name', transform: (val) => val || '-' },
    { header: 'Status', key: 'is_active', transform: (val) => (val ? 'Active' : 'Inactive') }
  ];

  const onExport = () => {
    handleExport({
      module: 'pbx',
      method: 'pbxAccountList',
      payload: { clientId },
      fileNamePrefix: 'all_pbx_accounts',
      sheetName: 'All PBX Accounts',
      getDataFromResponse: (res) => res.data.data.accounts || [],
      columns: exportColumns,
      onError: (msg) => showWarning(msg || 'No data to export')
    });
  };

  // ---- Refresh handler ----
  const handleRefresh = () => {
    setRefreshKey(prev => prev + 1);
  };

  // ---- Agent options for Create form dropdown (name only) ----
  const agentOptions = [
    { value: '', label: 'Select Agent' },
    ...agents.map(a => ({
      value: String(a.id),
      label: a.username
    }))
  ];

  // ---- Table columns config ----
  const headCells = [
    { id: 'id', numeric: false, disablePadding: false, label: 'ID' },
    {
      id: 'user_id',
      numeric: false,
      disablePadding: false,
      label: 'Agent',
      customRow: row => agentsById[String(row.user_id)] || row.user_id || '-'
    },
    { id: 'extension', numeric: false, disablePadding: false, label: 'Extension' },
    {
      id: 'sip_username',
      numeric: false,
      disablePadding: false,
      label: 'SIP Username',
      msg: true,
      truncateLength: 20
    },
    {
      id: 'sip_realm',
      numeric: false,
      disablePadding: false,
      label: 'SIP Realm',
      msg: true,
      truncateLength: 25
    },
    {
      id: 'ws_server',
      numeric: false,
      disablePadding: false,
      label: 'WS Server',
      msg: true,
      truncateLength: 30
    },
    {
      id: 'display_name',
      numeric: false,
      disablePadding: false,
      label: 'Display Name',
      customRow: row => row.display_name || '-'
    },
    {
      id: 'is_active',
      numeric: false,
      disablePadding: false,
      label: 'Status',
      disableSort: true,
      pill: true,
      pillConfig: {
        mapping: (val) =>
          val
            ? {
              label: 'Active',
              color: '#065F46',
              bgColor: '#D1FAE5',
              icon: <CheckCircleIcon style={{ fontSize: 16 }} />
            }
            : {
              label: 'Inactive',
              color: '#991B1B',
              bgColor: '#FEE2E2',
              icon: <CancelIcon style={{ fontSize: 16 }} />
            }
      }
    },
    { id: 'edit', label: 'Edit' },
    { id: 'delete', label: 'Delete' }
  ];

  // ---- Heading (left-aligned via flex:1 wrapper inside middleContent) ----
  const headingContent = (
    <div className={classes.headingWrapper}>
      <Typography variant="h6" className={classes.heading}>
        Call Center Users
      </Typography>
    </div>
  );

  // ---- Create button ----
  const createButton = (
    <ButtonOmni
      onClick={handleCreateClick}
      variant="outlined"
      size="small"
      label={
        <Box display="flex" alignItems="center" gap={1}>
          <AddIcon fontSize="small" />
          Create PBX Account
        </Box>
      }
    />
  );

  // ---- Create Dialog Content ----
  const createDialogContent = (
    <div className={classes.formContent}>
      <div className={classes.formRow}>
        <FormFieldOmni
          label="Agent *"
          variant="outlined"
          value={createFormData.userId}
          onChange={(e) => handleCreateFormChange('userId', e.target.value)}
          type="select"
          options={agentOptions}
          size="small"
          fullWidth
        />
        <FormFieldOmni
          label="Extension *"
          variant="outlined"
          value={createFormData.extension}
          onChange={(e) => handleCreateFormChange('extension', e.target.value)}
          size="small"
          fullWidth
          placeholder="1102"
        />
      </div>
      <div className={classes.formRow}>
        <FormFieldOmni
          label="SIP Username *"
          variant="outlined"
          value={createFormData.sipUsername}
          onChange={(e) => handleCreateFormChange('sipUsername', e.target.value)}
          size="small"
          fullWidth
          placeholder="1102"
        />
        <FormFieldOmni
          label="SIP Password *"
          variant="outlined"
          value={createFormData.sipPassword}
          onChange={(e) => handleCreateFormChange('sipPassword', e.target.value)}
          type="password"
          size="small"
          fullWidth
          placeholder="Enter SIP password"
        />
      </div>
      <div className={classes.formRow}>
        <FormFieldOmni
          label="SIP Realm *"
          variant="outlined"
          value={createFormData.sipRealm}
          onChange={(e) => handleCreateFormChange('sipRealm', e.target.value)}
          size="small"
          fullWidth
          placeholder="uat_web.test.com"
        />
        <FormFieldOmni
          label="WS Server *"
          variant="outlined"
          value={createFormData.wsServer}
          onChange={(e) => handleCreateFormChange('wsServer', e.target.value)}
          size="small"
          fullWidth
          placeholder="wss://uat_web.test.com:8090"
        />
      </div>
      <div className={classes.formRow}>
        <FormFieldOmni
          label="Display Name *"
          variant="outlined"
          value={createFormData.displayName}
          onChange={(e) => handleCreateFormChange('displayName', e.target.value)}
          size="small"
          fullWidth
          placeholder="Enter display name"
        />
        <FormFieldOmni
          label="Status"
          variant="outlined"
          value={String(createFormData.isActive)}
          onChange={(e) => handleCreateFormChange('isActive', e.target.value === 'true')}
          type="select"
          options={ACTIVE_STATUS_OPTIONS}
          size="small"
          fullWidth
        />
      </div>
    </div>
  );

  const createDialogActions = (
    <div className={classes.formActions}>
      <ButtonOmni
        onClick={handleCreateClose}
        variant="outlined"
        label="Cancel"
        disabled={isCreating}
      />
      <ButtonOmni
        onClick={handleCreateConfirm}
        variant="outlined"
        label={isCreating ? (
          <Box display="flex" alignItems="center" gap={1}>
            <CircularProgress size={16} color="inherit" />
            <span>Creating...</span>
          </Box>
        ) : 'Create'}
        disabled={isCreating}
      />
    </div>
  );

  // ---- Edit Dialog Content ----
  const editDialogContent = (
    <div className={classes.formContent}>
      <div className={classes.formRow}>
        <FormFieldOmni
          label="Agent *"
          variant="outlined"
          value={editFormData.userId}
          onChange={(e) => handleEditFormChange('userId', e.target.value)}
          type="select"
          options={agentOptions}
          size="small"
          fullWidth
        />
        <FormFieldOmni
          label="Extension *"
          variant="outlined"
          value={editFormData.extension}
          onChange={(e) => handleEditFormChange('extension', e.target.value)}
          size="small"
          fullWidth
          placeholder="1102"
        />
      </div>
      <div className={classes.formRow}>
        <FormFieldOmni
          label="SIP Username *"
          variant="outlined"
          value={editFormData.sipUsername}
          onChange={(e) => handleEditFormChange('sipUsername', e.target.value)}
          size="small"
          fullWidth
          placeholder="1102"
        />
        <FormFieldOmni
          label="SIP Password"
          variant="outlined"
          value={editFormData.sipPassword}
          onChange={(e) => handleEditFormChange('sipPassword', e.target.value)}
          type="password"
          size="small"
          fullWidth
          placeholder="Leave blank to keep current password"
        />
      </div>
      <div className={classes.formRow}>
        <FormFieldOmni
          label="SIP Realm *"
          variant="outlined"
          value={editFormData.sipRealm}
          onChange={(e) => handleEditFormChange('sipRealm', e.target.value)}
          size="small"
          fullWidth
          placeholder="uat_web.test.com"
        />
        <FormFieldOmni
          label="WS Server *"
          variant="outlined"
          value={editFormData.wsServer}
          onChange={(e) => handleEditFormChange('wsServer', e.target.value)}
          size="small"
          fullWidth
          placeholder="wss://uat_web.test.com:8090"
        />
      </div>
      <div className={classes.formRow}>
        <FormFieldOmni
          label="Display Name *"
          variant="outlined"
          value={editFormData.displayName}
          onChange={(e) => handleEditFormChange('displayName', e.target.value)}
          size="small"
          fullWidth
          placeholder="Enter display name"
        />
        <FormFieldOmni
          label="Status"
          variant="outlined"
          value={String(editFormData.isActive)}
          onChange={(e) => handleEditFormChange('isActive', e.target.value === 'true')}
          type="select"
          options={ACTIVE_STATUS_OPTIONS}
          size="small"
          fullWidth
        />
      </div>
    </div>
  );

  const editDialogActions = (
    <div className={classes.formActions}>
      <ButtonOmni
        onClick={handleEditClose}
        variant="outlined"
        label="Cancel"
        disabled={isEditing}
      />
      <ButtonOmni
        onClick={handleEditConfirm}
        variant="outlined"
        label={isEditing ? (
          <Box display="flex" alignItems="center" gap={1}>
            <CircularProgress size={16} color="inherit" />
            <span>Updating...</span>
          </Box>
        ) : 'Update'}
        disabled={isEditing}
      />
    </div>
  );

  return (
    <div className={classes.content}>
      <PageHeading icon={HeadsetMicIcon} title="Call Center Users" />
      {/* Refresh / Create / Export */}
      <DateFilterWithExport
        showDateRange={false}
        showExport={true}
        onExport={onExport}
        isExporting={isExporting}
        showRefresh={true}
        onRefresh={handleRefresh}
        customRightContent={createButton}
      />

      {/* Table */}
      <TableOmniNewAnimated
        rows={accounts}
        headCells={headCells}
        perPageRows={accounts.length || 10}
        totalItems={accounts.length}
        curPage={0}
        loading={isLoading && !isCreating && !isEditing && !isDeleting}
        isLoadingPagination={true}
        fixedHeight={true}
        maxTableHeight="600px"
        onDeleteClick={handleDeleteClick}
        onEditClick={handleEditClick}
      />

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        onClose={handleDeleteClose}
        onConfirm={handleDeleteConfirm}
        title="Delete PBX Account"
        message={`Are you sure you want to delete PBX account "${selectedAccount?.display_name || selectedAccount?.extension || ''}"? This action cannot be undone.`}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        loading={isDeleting}
      />

      {/* Create PBX Account Dialog */}
      <ModalOmni
        open={createDialogOpen}
        handleClose={handleCreateClose}
        title="Create PBX Account"
        content={createDialogContent}
        actions={createDialogActions}
        customWidth={650}
        disableBackdropClick={isCreating}
        disableEscapeKeyDown={isCreating}
      />

      {/* Edit PBX Account Dialog */}
      <ModalOmni
        open={editDialogOpen}
        handleClose={handleEditClose}
        title="Edit PBX Account"
        content={editDialogContent}
        actions={editDialogActions}
        customWidth={650}
        disableBackdropClick={isEditing}
        disableEscapeKeyDown={isEditing}
      />

      <SnackbarOmni {...snackbar} />
    </div>
  );
}

export default CallCenterUsers;